import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './NavigationMenu.module.scss';
import { useHistory } from 'react-router-dom';
import {
  COMMON_NAV_MENU_ITEMS,
} from 'src/shared/constants/navigationConstants';
import { GroupButton, RouteButton } from '../NavigationItem/NavigationItem';
import { Stack } from '@mui/material';

const ITEM_TEXT_STYLE = {
  fontSize: '13px',
  fontWeight: '550',
  fontFamily:
    'InterVariable, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
};

const groupItemsByCategory = (items) =>
  items.reduce((acc, item) => {
    const { category } = item;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

const hasActiveDescendant = (item, path) => {
  if (item.path === path) return true;
  if (!item.children) return false;
  return item.children.some((child) => hasActiveDescendant(child, path));
};

function NavBranch({
  item,
  currentPath,
  sidebarExpanded,
  onNavigate,
  onRequestExpand,
  selectedGroupKey,
  setSelectedGroupKey,
  parentKey = 'root',
  depth = 0,
}) {
  const { name, icon, children, buttonSx } = item;
  const nodeKey = `${parentKey}/${item.path || item.name}`;
  const hasChildren = Array.isArray(children) && children.length > 0;

  const hasActiveChild = useMemo(
    () => (hasChildren ? hasActiveDescendant(item, currentPath) : false),
    [hasChildren, item, currentPath]
  );

  const [isOpen, setIsOpen] = useState(hasActiveChild);
  const isSelectedGroup = selectedGroupKey === nodeKey;
  const shouldHighlightGroup = isOpen && isSelectedGroup && !hasActiveChild;

  useEffect(() => {
    if (hasChildren && hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasChildren, hasActiveChild]);

  const handleToggle = useCallback(() => {
    if (!sidebarExpanded) {
      setIsOpen(true);
      setSelectedGroupKey(nodeKey);
      if (onRequestExpand) {
        onRequestExpand();
      }
      return;
    }
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        setSelectedGroupKey(nodeKey);
      } else if (isSelectedGroup) {
        setSelectedGroupKey(null);
      }
      return next;
    });
  }, [sidebarExpanded, onRequestExpand, isSelectedGroup, nodeKey, setSelectedGroupKey]);

  if (!hasChildren) {
    return (
      <RouteButton
        isActive={currentPath === item.path}
        onClick={onNavigate}
        path={item.path}
        title={item.name}
        startIcon={depth === 0 ? item.icon : null}
        expanded={sidebarExpanded}
        params={item.params}
        disabled={item.disabled}
        nested={depth > 0}
        depth={depth}
        sx={buttonSx}
      >
        <span style={ITEM_TEXT_STYLE}>{item.name}</span>
      </RouteButton>
    );
  }

  return (
    <Stack>
      <GroupButton
        title={name}
        expanded={sidebarExpanded}
        startIcon={depth === 0 ? icon : null}
        isOpen={isOpen}
        isActive={shouldHighlightGroup}
        onToggle={handleToggle}
        depth={depth}
        showActiveMarker={!(depth === 0 && name === 'Compare')}
        sx={buttonSx}
      >
        <span style={ITEM_TEXT_STYLE}>{name}</span>
      </GroupButton>

      {sidebarExpanded && (
        <div
          className={`${styles.branchChildren} ${isOpen ? styles.branchChildrenOpen : ''}`}
          aria-hidden={!isOpen}
        >
          <div className={styles.branchChildrenInner}>
            {children.map((child) => (
              <NavBranch
                key={`${child.path || child.name}-${depth + 1}`}
                item={child}
                currentPath={currentPath}
                sidebarExpanded={sidebarExpanded}
                onNavigate={onNavigate}
                onRequestExpand={onRequestExpand}
                selectedGroupKey={selectedGroupKey}
                setSelectedGroupKey={setSelectedGroupKey}
                parentKey={nodeKey}
                depth={depth + 1}
              />
            ))}
          </div>
        </div>
      )}
    </Stack>
  );
}

export default function NavigationMenu({ expanded, onRequestExpand }) {
  const history = useHistory();
  const [currentPath, setCurrentPath] = useState(history.location.pathname);
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);

  const onClick = useCallback(
    (path) => {
      history.push(path);
    },
    [history]
  );

  useEffect(() => {
    setCurrentPath(history.location.pathname);
    setSelectedGroupKey(null);
    const unlisten = history.listen((location) => {
      setCurrentPath(location.pathname);
      setSelectedGroupKey(null);
    });
    return unlisten;
  }, [history]);

  // Filter out the 'Home' item as it is displayed in the sidebar header
  const NAV_MENU_ITEMS = COMMON_NAV_MENU_ITEMS.filter((item) => item.name !== 'Home');

  const groupedMenuItems = groupItemsByCategory(NAV_MENU_ITEMS);

  const renderMenuItem = (item) => (
    <NavBranch
      key={`${item.path || item.name}`}
      item={item}
      currentPath={currentPath}
      sidebarExpanded={expanded}
      onNavigate={onClick}
      onRequestExpand={onRequestExpand}
      selectedGroupKey={selectedGroupKey}
      setSelectedGroupKey={setSelectedGroupKey}
      parentKey="root"
      depth={0}
    />
  );

  return (
    <div className={styles.root}>
      {Object.keys(groupedMenuItems).map((category) => (
        <React.Fragment key={category}>
          {expanded && category != 'Home' && (
            <div className={styles.categoryTitle}>
              <span className={styles.categoryTitleText}>{category}</span>
            </div>
          )}
          {groupedMenuItems[category].map(renderMenuItem)}
        </React.Fragment>
      ))}
    </div>
  );
}
